import {Box, Grid} from '@mui/material';
import Container from '@mui/material/Container';
import {useCallback, useEffect, useState} from 'react';
import Button from '@mui/material/Button';
import PageHeadline from '../../components/PageHeadline';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router';
import AccessControl, {UserPermissions} from '../../components/shared/AccessControl';
import {FeatureName} from '../../../paths';
import {PageStickyHeader} from '../PageStickyHeader';
import {PageTopActions} from '../PageTopActions';
import ConfirmationDialog from '../../components/dialogs/ConfirmationDialog';
import {FormBuilder} from '../../form/FormBuilder';
import {useFormAPI} from '../../form/FormAPI';
import {usePermissions, UsePermissionState} from '../UsePermissions';
import {CallerIdConfig, CallerIdConfigListItem} from '../../model/CallerIdConfig';
import {onRejectSubmit} from '../../form/errorHandler';
import {OnSubmitHandler} from '../../form/model';
import {FormState} from '../../form/state/FormState';
import {TestAttributes} from '../../TestAttributes';
import NotificationService, {NotificationType} from '../../services/NotificationService';
import {useNavigate} from 'react-router-dom';
import {difference} from '../../form/utils';
import {Skeleton} from '@mui/lab';
import {ResponseListWrapper} from '../../services/model/ResponseListWrapper';
import {useAxiosContext} from '../../context/AxiosContext';
import {useLoading} from '../../context/LoadingContext';

function CallerIdConfigPage() {
  const {id} = useParams();
  const {t} = useTranslation();
  const {setLoading} = useLoading();
  const {useAxiosCallerIdConfigsAPI} = useAxiosContext();
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState<boolean>(false);
  const [isDeactivatedModalOpen, setIsDeactivatedModalOpen] = useState<boolean>(false);
  const {userPermissions}: UsePermissionState = usePermissions(FeatureName.CALLER_ID_CONFIGS);
  const [callerIdConfig, setCallerIdConfig] = useState<CallerIdConfig | null>(null);
  const formAPI = useFormAPI();

  const navigate = useNavigate();

  const [, getEntityList] = useAxiosCallerIdConfigsAPI<ResponseListWrapper<CallerIdConfigListItem>>(
    {url: 'caller-id-configs'},
    {
      manual: true,
    }
  );
  const [{data: entityData, loading: isGetEntityLoading, error: getEntityError}, getEntityData] =
    useAxiosCallerIdConfigsAPI<CallerIdConfig>(`/caller-id-configs/${id}`, {manual: true});

  interface ResourceIdResponse {
    id: string;
    message?: string;
  }

  const [{error: patchEntityError, loading: isPatchEntityLoading, response: patchEntityResponse}, patchEntityData] =
    useAxiosCallerIdConfigsAPI<ResourceIdResponse>(
      {url: `/caller-id-configs/${id}`, method: 'PATCH', params: {formatted: true}},
      {manual: true}
    );

  const [{error: postEntityError, loading: isPostEntityLoading, response: postEntityResponse}, postEntityData] =
    useAxiosCallerIdConfigsAPI<ResourceIdResponse>({url: `caller-id-configs`, method: 'POST'}, {manual: true});

  const [{error: deleteEntityError, loading: isDeleteEntityLoading, response: deleteEntityResponse}, deleteEntity] =
    useAxiosCallerIdConfigsAPI<ResourceIdResponse>({url: `/caller-id-configs/${id}`, method: 'delete'}, {manual: true});

  const onSubmit: OnSubmitHandler = useCallback(
    (formData: Partial<CallerIdConfig>, state: FormState) => {
      if (id) {
        formData = difference(formData, callerIdConfig);
        patchEntityData({
          data: formData,
        }).then(() => {
          getEntityData();
        });
      } else {
        postEntityData({
          data: formData,
        }).then((response) => {
          if (response?.status === 201) {
            getEntityList().then(() => {
              NotificationService.getInstance().sendNotification(
                response.data?.['message'] ?? 'success',
                NotificationType.SUCCESS
              );
              navigate(`/${FeatureName.CALLER_ID_CONFIGS}/${response.data['id']}`);
            });
          }
        });
      }
    },
    [callerIdConfig]
  );

  useEffect(() => {
    const loading = isGetEntityLoading || isPostEntityLoading || isPatchEntityLoading || isDeleteEntityLoading;
    setLoading(loading, 'CallerIdConfigPage');
  }, [isGetEntityLoading, isPostEntityLoading, isPatchEntityLoading, isDeleteEntityLoading]);

  useEffect(() => {
    const entityError = patchEntityError || postEntityError || getEntityError || deleteEntityError;
    if (entityError) {
      NotificationService.getInstance().sendNotification(entityError?.response?.data?.message, NotificationType.ERROR);
    }
  }, [patchEntityError, postEntityError, getEntityError, deleteEntityError]);

  useEffect(() => {
    if (id) {
      getEntityData();
    } else {
      setCallerIdConfig(null);
    }
  }, [id]);

  useEffect(() => {
    if (entityData) {
      setCallerIdConfig(entityData);
    }
  }, [entityData]);

  useEffect(() => {
    if (patchEntityResponse?.status === 200) {
      getEntityList().then(() => {
        NotificationService.getInstance().sendNotification(
          patchEntityResponse.data?.['message'] ?? 'success',
          NotificationType.SUCCESS
        );
      });
    }
  }, [patchEntityResponse]);

  useEffect(() => {
    if (deleteEntityResponse?.status === 200) {
      getEntityList().then(() => {
        NotificationService.getInstance().sendNotification(
          deleteEntityResponse?.data?.['message'] ?? 'success',
          NotificationType.SUCCESS
        );
        navigate(`/${FeatureName.CALLER_ID_CONFIGS}`);
      });
    }
  }, [deleteEntityResponse]);

  return (
    <Box
      sx={{
        backgroundColor: 'background.default',
        minHeight: '100%',
        py: 3,
      }}
    >
      <Container maxWidth={false}>
        <Grid container spacing={3}>
          <PageStickyHeader>
            <Grid container item xs={12} rowSpacing={{xs: 3, sm: 3}}>
              <Grid item xs={12} md={6} sx={{display: 'flex', alignItems: 'center'}}>
                <PageHeadline>{t('caller-id-configs.headline')}</PageHeadline>
              </Grid>
              <Grid item xs={12} md={6}>
                <PageTopActions>
                  {id && (
                    <AccessControl userPermissions={userPermissions} allowedPermissions={[UserPermissions.DELETE]}>
                      <ConfirmationDialog
                        message={t('caller-id-configs.deactivate-message')}
                        headline={t('caller-id-configs.deactivate')}
                        isDialogOpen={isDeactivatedModalOpen}
                        handleClose={() => setIsDeactivatedModalOpen(false)}
                      >
                        <Button
                          onClick={() => {
                            deleteEntity();
                            setIsDeactivatedModalOpen(false);
                          }}
                          {...{[TestAttributes.BUTTON_NAME]: 'confirmation-dialog-action-btn'}}
                          id="confirmation-dialog-action-btn"
                        >
                          {t('caller-id-configs.deactivate')}
                        </Button>
                      </ConfirmationDialog>

                      <Button
                        {...{[TestAttributes.BUTTON_NAME]: 'confirmation-dialog-btn'}}
                        id={'confirmation-dialog-btn'}
                        color="secondary"
                        onClick={() => setIsDeactivatedModalOpen(true)}
                      >
                        {t('caller-id-configs.deactivate')}
                      </Button>
                    </AccessControl>
                  )}
                  <Button
                    {...{[TestAttributes.BUTTON_NAME]: 'confirmation-dialog-btn'}}
                    id={'confirmation-dialog-btn'}
                    color="secondary"
                    onClick={() => setIsConfirmationDialogOpen(true)}
                  >
                    {t('shared.clear')}
                  </Button>

                  <AccessControl
                    userPermissions={userPermissions}
                    allowedPermissions={[id ? UserPermissions.MODIFY : UserPermissions.CREATE]}
                  >
                    <Button
                      {...{[TestAttributes.BUTTON_NAME]: 'save-btn'}}
                      id={'save-btn'}
                      onClick={() => {
                        formAPI.submit();
                      }}
                    >
                      {id ? t('shared.update') : t('shared.save')}
                    </Button>
                  </AccessControl>
                </PageTopActions>
              </Grid>
            </Grid>
          </PageStickyHeader>
          <Grid item xs={12}>
            {isGetEntityLoading ? (
              <Skeleton variant="rectangular" height={500} />
            ) : (
              <FormBuilder
                onRejectSubmit={onRejectSubmit}
                formId={FeatureName.CALLER_ID_CONFIGS}
                api={formAPI}
                initialValues={callerIdConfig}
                onSubmit={onSubmit}
              />
            )}
          </Grid>
        </Grid>
        <ConfirmationDialog
          message={t('shared.clear-form-modal-content')}
          headline={t('shared.clear-form-modal-headline')}
          isDialogOpen={isConfirmationDialogOpen}
          handleClose={() => setIsConfirmationDialogOpen(false)}
        >
          <Button
            onClick={() => {
              formAPI.reset();
              setIsConfirmationDialogOpen(false);
            }}
            {...{[TestAttributes.BUTTON_NAME]: 'confirmation-dialog-action-btn'}}
            id="confirmation-dialog-action-btn"
          >
            {t('shared.accept')}
          </Button>
        </ConfirmationDialog>
      </Container>
    </Box>
  );
}

export default CallerIdConfigPage;
